import {
  AudienceCurveVolumeDataApi,
  AudiencePreviewAccuracyApi,
  AudiencePreviewPointApi
} from '../../../services/audience-api.service';
import { AudiencePreviewAccuracyView } from '../create/audience-create.component';
import { AudiencePreviewAccuracy, AudiencePreviewPoint } from './audience.types';
import { toNumericValue } from './audience-format';

// Normalizzazione della curva di accuratezza (preview / curve volume) in punti per il grafico.
// Estratta da AudienceComponent: comportamento identico, funzioni pure.

export const PREVIEW_DISTANCE_MIN = 0.005;
export const PREVIEW_DISTANCE_MAX = 0.35;
// Ampiezza minima della finestra di zoom (vista non-full): evita che la finestra "a specchio"
// collassi quando SuggestedPoint e InflectionPoint (quasi) coincidono su curve molto ripide.
export const PREVIEW_MIN_WINDOW = 0.1;

export function normalizePreviewAccuracy(raw: AudiencePreviewAccuracyApi | null | undefined): AudiencePreviewAccuracy | null {
  if (!raw) {
    return null;
  }

  const sourcePoints = extractPreviewPoints(raw);

  const normalizedPoints = sourcePoints
    .map((point, index) => normalizePreviewPoint(point, index))
    .filter((point): point is AudiencePreviewPoint => !!point);

  if (!normalizedPoints.length) {
    return null;
  }

  const visiblePoints = limitPreviewPointsForChart(normalizedPoints);
  if (!visiblePoints.length) {
    return null;
  }

  const maxIndex = visiblePoints.length - 1;
  const suggestedStep = toNumericValue(raw.SuggestedPoint);
  const inflectionPoint = toNumericValue(raw.InflectionPoint);
  const defaultStep = clampPreviewStep(suggestedStep ?? raw.defaultStep ?? inflectionPoint, maxIndex);
  const selectedStep = clampPreviewStep(raw.selectedStep ?? suggestedStep ?? raw.defaultStep ?? inflectionPoint, maxIndex);

  return {
    points: visiblePoints,
    defaultStep,
    selectedStep,
    suggestedStep: clampPreviewStep(suggestedStep ?? defaultStep, maxIndex)
  };
}

export function mapCurveVolumeToPreviewAccuracy(
  raw: AudienceCurveVolumeDataApi | null | undefined,
  selectedQualityIndex?: number | null,
  fullRange = false
): AudiencePreviewAccuracy | null {
  if (!raw?.Points?.length) {
    return null;
  }

  const normalizedPoints = raw.Points
    .map((point, index) => {
      const distance = toNumericValue(point?.Distance);
      const users = toNumericValue(point?.TNCIDs);
      if (distance === null || users === null || !Number.isFinite(distance) || !Number.isFinite(users)) {
        return null;
      }
      const minDistance = normalizeCurveDistance(distance);

      return {
        step: index,
        users: Math.max(0, Math.round(users)),
        minDistance,
        maxDistance: null,
        label: buildPreviewRangeLabel(minDistance, null)
      } as AudiencePreviewPoint;
    })
    .filter((point): point is AudiencePreviewPoint => !!point);

  if (!normalizedPoints.length) {
    return null;
  }

  const suggestedPoint = toNumericValue(raw.SuggestedPoint);
  const inflectionPoint = toNumericValue(raw.InflectionPoint);
  const persistedQualityIndex = toNumericValue(selectedQualityIndex);
  // La finestra di zoom si centra sull'AudienceQualityIndex salvato, se c'è
  const anchor = persistedQualityIndex ?? suggestedPoint ?? inflectionPoint ?? null;
  // Le curve arrivano fino a distanza 2: con gli embedding centrati ('centered-centroid') le distanze
  // si concentrano intorno a 1. Limite = ultimo punto in cui il volume cresce ancora, mai sotto 1.
  const curveMax = getCurveGrowthMaxDistance(normalizedPoints);

  let effectiveMin: number | undefined;
  let effectiveMax: number | undefined;

  if (fullRange) {
    effectiveMin = PREVIEW_DISTANCE_MIN;
    effectiveMax = curveMax;
  } else if (suggestedPoint !== null && inflectionPoint !== null) {
    // RightLimit = mirror of SuggestedPoint around InflectionPoint
    const rightLimit = inflectionPoint + (inflectionPoint - suggestedPoint);
    // LeftLimit = mirror of InflectionPoint around SuggestedPoint, clamped to PREVIEW_DISTANCE_MIN
    const leftLimit = Math.max(2 * suggestedPoint - inflectionPoint, PREVIEW_DISTANCE_MIN);
    effectiveMin = leftLimit;
    effectiveMax = Math.max(rightLimit, suggestedPoint);
  } else if (anchor !== null) {
    // Senza flesso (es. 'centered-centroid'): finestra dall'inizio della curva al doppio dell'anchor,
    // così l'anchor sta al centro (almeno fino a PREVIEW_DISTANCE_MAX)
    effectiveMin = Math.min(PREVIEW_DISTANCE_MIN, anchor);
    effectiveMax = Math.min(Math.max(PREVIEW_DISTANCE_MAX, 2 * anchor - PREVIEW_DISTANCE_MIN), Math.max(curveMax, anchor));
  }

  // Curve molto ripide (es. pesate) hanno SuggestedPoint≈InflectionPoint: la finestra "a specchio"
  // collasserebbe e il grafico degenererebbe in una linea verticale. Garantiamo un'ampiezza minima
  // centrata sull'anchor, clampata a [PREVIEW_DISTANCE_MIN, curveMax].
  if (!fullRange && effectiveMin !== undefined && effectiveMax !== undefined && (effectiveMax - effectiveMin) < PREVIEW_MIN_WINDOW) {
    const center = anchor ?? (effectiveMin + effectiveMax) / 2;
    let lo = Math.max(center - PREVIEW_MIN_WINDOW / 2, PREVIEW_DISTANCE_MIN);
    const hi = Math.min(lo + PREVIEW_MIN_WINDOW, curveMax);
    lo = Math.max(hi - PREVIEW_MIN_WINDOW, PREVIEW_DISTANCE_MIN);
    effectiveMin = lo;
    effectiveMax = hi;
  }

  // L'AudienceQualityIndex salvato può essere diverso dal punto suggerito (scelto a mano):
  // si allarga la finestra perché il punto selezionato sia sempre nel grafico
  if (!fullRange && persistedQualityIndex !== null && effectiveMin !== undefined && effectiveMax !== undefined) {
    effectiveMin = Math.max(Math.min(effectiveMin, persistedQualityIndex), PREVIEW_DISTANCE_MIN);
    effectiveMax = Math.min(Math.max(effectiveMax, persistedQualityIndex), curveMax);
  }

  const visiblePoints = limitPreviewPointsForChart(
    normalizedPoints,
    effectiveMin,
    effectiveMax
  );
  if (!visiblePoints.length) {
    return null;
  }

  const maxIndex = visiblePoints.length - 1;
  const defaultTarget = suggestedPoint ?? inflectionPoint ?? visiblePoints[0].minDistance;
  const selectedTarget = persistedQualityIndex ?? suggestedPoint ?? inflectionPoint ?? visiblePoints[0].minDistance;
  const defaultStep = findClosestCurveStep(visiblePoints, defaultTarget);
  const selectedStep = findClosestCurveStep(visiblePoints, selectedTarget);

  return {
    points: visiblePoints,
    defaultStep: clampPreviewStep(defaultStep, maxIndex),
    selectedStep: clampPreviewStep(selectedStep, maxIndex),
    suggestedStep: clampPreviewStep(findClosestCurveStep(
      visiblePoints,
      suggestedPoint ?? inflectionPoint ?? visiblePoints[0].minDistance
    ), maxIndex)
  };
}

/** Distanza dell'ultimo punto in cui il volume cumulato cresce ancora, mai sotto 1. */
export function getCurveGrowthMaxDistance(points: AudiencePreviewPoint[]): number {
  let max = 1;
  const sorted = [...points].sort((a, b) => a.minDistance - b.minDistance);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].users > sorted[i - 1].users) {
      max = Math.max(max, sorted[i].minDistance);
    }
  }
  return max;
}

export function findClosestCurveStep(points: AudiencePreviewPoint[], target: number | null): number {
  if (target === null || !points.length) {
    return 0;
  }

  let closestIndex = 0;
  let closestDistance = Infinity;

  points.forEach((point, index) => {
    const distance = Math.abs(point.minDistance - target);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
}

export function limitPreviewPointsForChart(
  points: AudiencePreviewPoint[],
  minOverride?: number,
  maxOverride?: number
): AudiencePreviewPoint[] {
  const effectiveMin = minOverride !== undefined ? minOverride : PREVIEW_DISTANCE_MIN;
  const effectiveMax = maxOverride !== undefined ? maxOverride : PREVIEW_DISTANCE_MAX;
  const filtered = points
    .filter(point => point.minDistance >= effectiveMin && point.minDistance <= effectiveMax)
    .sort((a, b) => a.minDistance - b.minDistance)
    .map(point => ({
      ...point,
      minDistance: normalizeCurveDistance(point.minDistance)
    }));

  return filtered.map((point, index) => {
    const nextPoint = filtered[index + 1] ?? null;
    const maxDistance = nextPoint ? normalizeCurveDistance(nextPoint.minDistance) : null;

    return {
      ...point,
      step: index,
      maxDistance,
      label: buildPreviewRangeLabel(point.minDistance, maxDistance)
    };
  });
}

export function extractPreviewPoints(raw: AudiencePreviewAccuracyApi): Array<AudiencePreviewPointApi | number> {
  const rawPoints = raw.Points ?? raw.points ?? null;

  if (typeof rawPoints === 'string') {
    try {
      const parsed = JSON.parse(rawPoints);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return Array.isArray(rawPoints) ? rawPoints : [];
}

export function normalizePreviewPoint(point: any, index: number): AudiencePreviewPoint | null {
  const minDistance = 0.1 + (index * 0.01);
  const fallbackMaxDistance = index === 24 ? null : Number((minDistance + 0.01).toFixed(2));

  if (typeof point === 'number') {
    return {
      step: index,
      users: Math.max(0, Math.round(point)),
      minDistance,
      maxDistance: fallbackMaxDistance,
      label: buildPreviewRangeLabel(minDistance, fallbackMaxDistance)
    };
  }

  if (!point || typeof point !== 'object') {
    return null;
  }

  const users = toNumericValue((point as any).users ?? (point as any).cumulativeUsers ?? (point as any).volume);
  if (!Number.isFinite(users)) {
    return null;
  }

  const rawMin = toNumericValue((point as any).minDistance ?? (point as any).MinDistance);
  const rawMax = toNumericValue((point as any).maxDistance ?? (point as any).MaxDistance);
  const normalizedMin = rawMin !== null && Number.isFinite(rawMin) ? Number(rawMin.toFixed(2)) : minDistance;
  const normalizedMax = rawMax !== null && Number.isFinite(rawMax) ? Number(rawMax.toFixed(2)) : fallbackMaxDistance;

  return {
    step: clampPreviewStep((point as any).step ?? (point as any).Step ?? index, 24),
    users: Math.max(0, Math.round(users ?? 0)),
    minDistance: normalizedMin,
    maxDistance: normalizedMax,
    label: ((point as any).label || '').toString().trim() || buildPreviewRangeLabel(normalizedMin, normalizedMax)
  };
}

export function clampPreviewStep(value: unknown, max: number): number {
  const numeric = toNumericValue(value);
  if (numeric === null || !Number.isFinite(numeric)) {
    return 0;
  }
  return Math.min(Math.max(Math.round(numeric), 0), max);
}

export function buildPreviewRangeLabel(minDistance: number, maxDistance: number | null): string {
  if (maxDistance === null) {
    return `> ${formatPreviewDistance(minDistance)}`;
  }
  return `${formatPreviewDistance(minDistance)} - ${formatPreviewDistance(maxDistance)}`;
}

export function normalizeCurveDistance(value: number): number {
  return Number(value.toFixed(3));
}

export function formatPreviewDistance(value: number): string {
  return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

export function mapPreviewAccuracyToForm(
  preview: AudiencePreviewAccuracy,
  selectedQualityIndex?: number | null
): AudiencePreviewAccuracyView {
  const selectedStep = resolvePreviewStepFromQualityIndex(preview, selectedQualityIndex);
  return {
    points: preview.points.map(point => ({ ...point })),
    defaultStep: preview.defaultStep,
    selectedStep,
    suggestedStep: preview.suggestedStep
  };
}

export function resolvePreviewStepFromQualityIndex(
  preview: AudiencePreviewAccuracy,
  selectedQualityIndex?: number | null
): number {
  if (selectedQualityIndex === null || selectedQualityIndex === undefined) {
    return preview.selectedStep;
  }

  return findClosestCurveStep(preview.points, selectedQualityIndex);
}
